/** Annotation keys on product `Component` entities (`metadata.annotations`). */
export const PRODUCT_MASTER_MAPPINGS_PRODUCT_CODES_ANNOTATION =
  'moneyforward.com/mappings.product-codes';
export const PRODUCT_MASTER_MAPPINGS_SONARQUBE_ANNOTATION =
  'moneyforward.com/mappings.sonarqube';
/**
 * Kubernetes namespaces owned by the product. Kept separate from
 * `mappings.product-codes`: aliases are product identity (DFP lookup, MINT
 * try-list); namespaces are not.
 */
export const PRODUCT_MASTER_MAPPINGS_K8S_NAMESPACES_ANNOTATION =
  'moneyforward.com/mappings.k8s-namespaces';
/**
 * GitHub repositories stored as `org/repo`.
 * `backstage.io/source-location` and the CI slugs are derived from these by product_master.
 */
export const PRODUCT_MASTER_MAPPINGS_GITHUB_FRONTEND_ANNOTATION =
  'moneyforward.com/mappings.github.frontend';
export const PRODUCT_MASTER_MAPPINGS_GITHUB_BACKEND_ANNOTATION =
  'moneyforward.com/mappings.github.backend';
export const PRODUCT_MASTER_DEFAULT_GITHUB_ORG = 'moneyforward';

export interface PlatformMappings {
  productCodes: string[];
  sonarqube: string[];
  k8sNamespaces: string[];
  githubFrontend: string[];
  githubBackend: string[];
}

export const EMPTY_PLATFORM_MAPPINGS: PlatformMappings = {
  productCodes: [],
  sonarqube: [],
  k8sNamespaces: [],
  githubFrontend: [],
  githubBackend: [],
};

export const PLATFORM_MAPPINGS_PRODUCT_CODE_ALIASES_LABEL = 'Product Code Aliases';
export const PLATFORM_MAPPINGS_SONARQUBE_PROJECT_KEYS_LABEL = 'SonarQube Project Keys';
export const PLATFORM_MAPPINGS_K8S_NAMESPACES_LABEL = 'K8s Namespaces';
export const PLATFORM_MAPPINGS_GITHUB_FRONTEND_LABEL = 'GitHub frontend repository';
export const PLATFORM_MAPPINGS_GITHUB_BACKEND_LABEL = 'GitHub backend repository';

/**
 * GitHub organization login (same rules as a username), stored in lowercase.
 * 1–39 characters: letters, numbers, and single hyphens. No leading, trailing,
 * or repeated hyphen.
 */
const GITHUB_ORG_PATTERN = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){0,38}$/;
/**
 * GitHub repository name, stored in lowercase. Up to 100 characters: letters,
 * numbers, `.`, `-`, and `_`. `.` and `..` are not names.
 */
const GITHUB_REPO_PATTERN = /^[a-z0-9._-]{1,100}$/;

function isGithubOrg(org: string): boolean {
  return GITHUB_ORG_PATTERN.test(org);
}

function isGithubRepo(repo: string): boolean {
  return repo !== '.' && repo !== '..' && GITHUB_REPO_PATTERN.test(repo);
}

function githubOrgRepoFromUrl(text: string): [string, string] | undefined {
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return undefined;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (host !== 'github.com') {
    return undefined;
  }
  const parts = url.pathname.split('/').filter(Boolean);
  return parts.length >= 2 ? [parts[0], parts[1].replace(/\.git$/, '')] : undefined;
}

/**
 * Stored form of one GitHub repository. User input is `org/repo`.
 * Returns `undefined` when the input is not `org/repo`.
 */
export function normalizeGithubRepoEntry(raw: string): string | undefined {
  const text = raw.trim();
  const parts = text.split('/');
  if (parts.length !== 2) {
    return undefined;
  }
  const org = parts[0].trim();
  const repo = parts[1].trim().replace(/\.git$/, '');
  if (!isGithubOrg(org) || !isGithubRepo(repo)) {
    return undefined;
  }
  return `${org}/${repo}`;
}

/** `org/repo` for a stored entry (a leftover bare name belongs to the `moneyforward` org). */
export function githubRepoSlug(entry: string): string {
  return entry.includes('/')
    ? entry
    : `${PRODUCT_MASTER_DEFAULT_GITHUB_ORG}/${entry}`;
}

export function githubRepoUrl(entry: string): string {
  return `https://github.com/${githubRepoSlug(entry)}`;
}

/** Validation message for the TechDocs URL field; `undefined` when valid or empty. */
export function techDocsUrlValidationError(url: string): string | undefined {
  const text = url.trim();
  if (!text) {
    return undefined;
  }
  if (!text.startsWith('https://github.com/')) {
    return 'Must start with https://github.com/';
  }
  const orgRepo = githubOrgRepoFromUrl(text);
  if (!orgRepo || !isGithubOrg(orgRepo[0]) || !isGithubRepo(orgRepo[1])) {
    return 'Must be a lowercase GitHub repository (https://github.com/org/repo)';
  }
  return undefined;
}

/** Entries that are not valid GitHub repositories (for form and submit validation). */
export function invalidGithubRepoEntries(entries: readonly string[]): string[] {
  return entries.filter(entry => normalizeGithubRepoEntry(entry) === undefined);
}

/**
 * Submit-time check for the edit/create payload: TechDocs URL and GitHub repo lists.
 * Returns a user-facing message, or `undefined` when everything is valid.
 */
export function validateGithubRepositoryChanges(
  changes: Record<string, unknown>,
): string | undefined {
  const product = changes.product as { annotations?: Record<string, unknown> } | undefined;
  const techDocsRef = product?.annotations?.['backstage.io/techdocs-ref'];
  if (typeof techDocsRef === 'string') {
    const error = techDocsUrlValidationError(techDocsRef.replace(/^url:/, ''));
    if (error) {
      return `TechDocs URL is invalid: ${error}`;
    }
  }
  const mappings = changes.platformMappings as Record<string, unknown> | undefined;
  for (const [key, label] of [
    ['githubFrontend', PLATFORM_MAPPINGS_GITHUB_FRONTEND_LABEL],
    ['githubBackend', PLATFORM_MAPPINGS_GITHUB_BACKEND_LABEL],
  ] as const) {
    const value = mappings?.[key];
    if (!Array.isArray(value)) {
      continue;
    }
    const invalid = invalidGithubRepoEntries(
      value.filter((item): item is string => typeof item === 'string'),
    );
    if (invalid.length > 0) {
      return `${label} must be a lowercase org/repo. Invalid: ${invalid.join(', ')}`;
    }
  }
  return undefined;
}

const PLATFORM_MAPPINGS_DISPLAY_MISSING = '---';

function parseFromJsonOrCommaSeparated(raw: string): string[] {
  const items: string[] = [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      for (const item of parsed) {
        appendAnnotationString(items, item);
      }
      return items;
    }
  } catch {
    for (const part of raw.split(',')) {
      const s = part.trim();
      if (s) {
        items.push(s);
      }
    }
  }
  return items;
}

function appendAnnotationString(items: string[], item: unknown): void {
  const s = annotationItemToString(item);
  if (s) {
    items.push(s);
  }
}

function annotationItemToString(item: unknown): string {
  if (item === null || item === undefined) {
    return '';
  }
  switch (typeof item) {
    case 'string':
      return item.trim();
    case 'number':
      return Number.isFinite(item) ? `${item}`.trim() : '';
    case 'boolean':
      return item ? 'true' : 'false';
    case 'bigint':
      return `${item}`.trim();
    default:
      return '';
  }
}

/**
 * Parse a JSON string array annotation (or tolerate a plain YAML list when already parsed).
 */
export function parseStringArrayAnnotation(raw: unknown): string[] {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    return dedupePreserveOrder(parseFromJsonOrCommaSeparated(trimmed));
  }
  if (Array.isArray(raw)) {
    const items: string[] = [];
    for (const item of raw) {
      appendAnnotationString(items, item);
    }
    return dedupePreserveOrder(items);
  }
  return [];
}

function dedupePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

export function resolveProductCodeAliasesFromAnnotations(
  annotations: Record<string, unknown>,
): string[] {
  const fromNew = parseStringArrayAnnotation(
    annotations[PRODUCT_MASTER_MAPPINGS_PRODUCT_CODES_ANNOTATION],
  );
  if (fromNew.length > 0) {
    return fromNew;
  }
  return parseStringArrayAnnotation(annotations.productCodeMappings);
}

export function resolvePlatformMappingsFromAnnotations(
  annotations: Record<string, unknown>,
): PlatformMappings {
  return {
    productCodes: resolveProductCodeAliasesFromAnnotations(annotations),
    sonarqube: parseStringArrayAnnotation(
      annotations[PRODUCT_MASTER_MAPPINGS_SONARQUBE_ANNOTATION],
    ),
    k8sNamespaces: parseStringArrayAnnotation(
      annotations[PRODUCT_MASTER_MAPPINGS_K8S_NAMESPACES_ANNOTATION],
    ),
    githubFrontend: parseStringArrayAnnotation(
      annotations[PRODUCT_MASTER_MAPPINGS_GITHUB_FRONTEND_ANNOTATION],
    ),
    githubBackend: parseStringArrayAnnotation(
      annotations[PRODUCT_MASTER_MAPPINGS_GITHUB_BACKEND_ANNOTATION],
    ),
  };
}

function formatPlatformMappingsValueList(values: string[]): string {
  return values.length > 0 ? values.join(', ') : PLATFORM_MAPPINGS_DISPLAY_MISSING;
}

/** One labeled line for list/detail/export (e.g. `Product Code Aliases: a, b`). */
export function formatPlatformMappingsAliasesLine(values: string[]): string {
  return `${PLATFORM_MAPPINGS_PRODUCT_CODE_ALIASES_LABEL}: ${formatPlatformMappingsValueList(values)}`;
}

/** One labeled line for list/detail/export (e.g. `SonarQube Project Keys: a, b`). */
export function formatPlatformMappingsSonarLine(values: string[]): string {
  return `${PLATFORM_MAPPINGS_SONARQUBE_PROJECT_KEYS_LABEL}: ${formatPlatformMappingsValueList(values)}`;
}

/** One labeled line for list/detail/export (e.g. `K8s Namespaces: a, b`). */
export function formatPlatformMappingsK8sNamespacesLine(values: string[]): string {
  return `${PLATFORM_MAPPINGS_K8S_NAMESPACES_LABEL}: ${formatPlatformMappingsValueList(values)}`;
}

/** One labeled line for list/detail/export (e.g. `GitHub frontend repository: a, org/b`). */
export function formatPlatformMappingsGithubFrontendLine(values: string[]): string {
  return `${PLATFORM_MAPPINGS_GITHUB_FRONTEND_LABEL}: ${formatPlatformMappingsValueList(values)}`;
}

/** One labeled line for list/detail/export (e.g. `GitHub backend repository: a, org/b`). */
export function formatPlatformMappingsGithubBackendLine(values: string[]): string {
  return `${PLATFORM_MAPPINGS_GITHUB_BACKEND_LABEL}: ${formatPlatformMappingsValueList(values)}`;
}

/** Lowercased tokens for table / advanced search (every platform mapping list). */
export function buildPlatformMappingsSearchHaystack(mappings: PlatformMappings): string {
  return [
    ...mappings.productCodes,
    ...mappings.sonarqube,
    ...mappings.k8sNamespaces,
    ...mappings.githubFrontend,
    ...mappings.githubBackend,
  ]
    .join(' ')
    .toLowerCase()
    .replaceAll(/\s+/g, ' ')
    .trim();
}

export function hasAnyPlatformMappings(mappings: PlatformMappings): boolean {
  return (
    mappings.productCodes.length > 0 ||
    mappings.sonarqube.length > 0 ||
    mappings.k8sNamespaces.length > 0 ||
    mappings.githubFrontend.length > 0 ||
    mappings.githubBackend.length > 0
  );
}

/** Compact summary for the list Platform Mappings column. GitHub is its own column. */
export function formatPlatformMappingsTableSummary(mappings: PlatformMappings): string {
  return [
    formatPlatformMappingsAliasesLine(mappings.productCodes),
    formatPlatformMappingsSonarLine(mappings.sonarqube),
    formatPlatformMappingsK8sNamespacesLine(mappings.k8sNamespaces),
  ].join('\n');
}
